/**
 * Per-viewer subtitle delay controller.
 *
 * Re-times the active WebVTT track by mutating cue.startTime/cue.endTime in
 * place. Video.js removed trackTimeOffset in v4 (see #4212) and no replacement
 * ever merged, so direct cue mutation is the only supported route — this is
 * the Jellyfin-proven pattern documented in the master build prompt §WS-7.
 *
 * Key invariants:
 *  - Only the DELTA between the desired offset and what was already applied to
 *    a given track is ever written, so repeated adjustments never compound.
 *  - Each track keeps its own "applied" ledger; switching tracks re-applies the
 *    current desired offset to the newly active track from zero.
 *  - Negative offsets are supported (audio delay is the positive-only one).
 *  - This is local-only per viewer; it never broadcasts and never touches the
 *    server-authoritative playback state.
 *
 * @packageDocumentation
 */
import type { Player } from '../player/useVideoJs';
import { LIMITS } from '@syncreel/shared';

/** The controller surface exported for the composition root (§5.3). */
export interface SubtitleDelayController {
  /** Set the desired offset in seconds (clamped to ±SUBTITLE_OFFSET_RANGE_S). */
  setOffset(seconds: number): void;
  /** Read the current desired offset in seconds. */
  getOffset(): number;
  /** Undo every applied shift and reset the desired offset to 0. */
  reset(): void;
}

/** Text track kinds that carry rendered captions/subtitles. */
const SUBTITLE_KINDS = new Set(['subtitles', 'captions']);

/**
 * Create a subtitle delay controller bound to a Video.js player.
 *
 * The controller attaches listeners to the player's text track list so that
 * offsets are re-applied when the viewer switches subtitle tracks, and deferred
 * until a track's cues are available when the track is not yet loaded.
 */
export function createSubtitleDelayController(
  player: Player,
): SubtitleDelayController {
  // The viewer's desired offset (seconds). Persists across track switches.
  let desiredOffset = 0;

  // Per-track ledger of how much we have already shifted that track's cues,
  // keyed by the track object so it is garbage-collected with the track.
  const appliedByTrack = new WeakMap<TextTrack, number>();

  // Desired offsets waiting for a track whose cues are not yet loaded.
  const pendingByTrack = new WeakMap<TextTrack, number>();

  // video.js textTracks() returns its own TextTrackList; the native DOM
  // TextTrackList is structurally compatible for the members we use.
  const textTracks = player.textTracks() as unknown as TextTrackList;

  function activeSubtitleTrack(): TextTrack | undefined {
    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i] as TextTrack;
      if (SUBTITLE_KINDS.has(track.kind) && track.mode === 'showing') {
        return track;
      }
    }
    return undefined;
  }

  /**
   * Shift every cue of `track` by `delta` seconds.
   * Returns true if the track was loaded (cues available), false if the shift
   * must be deferred until the track loads.
   */
  function shiftCues(track: TextTrack, delta: number): boolean {
    const cues = track.cues;
    // WHY: cues === null means the track's data has not loaded yet (common with
    // segmented HLS WebVTT). Defer until loadeddata/cuechange fires.
    if (cues === null) return false;
    if (cues.length === 0 || delta === 0) return true;

    // WHY: Firefox renders stale cue positions when cues are mutated while the
    // track is showing. Toggling mode to 'hidden' and back forces a refresh.
    const prevMode = track.mode;
    track.mode = 'hidden';
    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i] as TextTrackCue;
      cue.startTime += delta;
      cue.endTime += delta;
    }
    // WHY: restoring the prior mode re-enables rendering and forces Firefox to
    // re-paint the (now shifted) active cue.
    track.mode = prevMode;
    return true;
  }

  /** Apply the current desired offset to a single track (delta only). */
  function applyToTrack(track: TextTrack): void {
    const applied = appliedByTrack.get(track) ?? 0;
    const delta = desiredOffset - applied;
    if (delta === 0) return;
    if (!shiftCues(track, delta)) {
      // Defer until the track's cues become available.
      pendingByTrack.set(track, desiredOffset);
      return;
    }
    appliedByTrack.set(track, desiredOffset);
    pendingByTrack.delete(track);
  }

  /** Retry a pending offset once a track's cues have loaded. */
  function retryPending(track: TextTrack): void {
    const pending = pendingByTrack.get(track);
    if (pending === undefined) return;
    const applied = appliedByTrack.get(track) ?? 0;
    if (shiftCues(track, pending - applied)) {
      appliedByTrack.set(track, pending);
      pendingByTrack.delete(track);
    }
  }

  // Re-apply the current offset whenever the active subtitle track changes
  // (e.g. the viewer switches language). Each track starts from applied 0.
  function onTextTracksChange(): void {
    const active = activeSubtitleTrack();
    if (active) applyToTrack(active);
  }

  function onAddTrack(event: TrackEvent): void {
    const track = event.track as TextTrack;
    if (!SUBTITLE_KINDS.has(track.kind)) return;
    attachTrackListeners(track);
  }

  function attachTrackListeners(track: TextTrack): void {
    // WHY: 'load' fires for video.js-managed tracks once cues populate; native
    // tracks may only fire 'cuechange'. Listening to both is harmless and
    // covers the segmented-WebVTT case where cues arrive incrementally.
    track.addEventListener('load', () => retryPending(track));
    track.addEventListener('cuechange', () => retryPending(track));
  }

  textTracks.addEventListener('change', onTextTracksChange);
  textTracks.addEventListener('addtrack', onAddTrack);
  for (let i = 0; i < textTracks.length; i++) {
    const track = textTracks[i] as TextTrack;
    if (SUBTITLE_KINDS.has(track.kind)) attachTrackListeners(track);
  }

  // As a last-resort safety net, retry pending offsets once the media's initial
  // data has loaded (covers tracks whose load event never fires).
  player.on('loadeddata', () => {
    const active = activeSubtitleTrack();
    if (active) retryPending(active);
  });

  return {
    setOffset(seconds: number): void {
      const clamped = Math.max(
        -LIMITS.SUBTITLE_OFFSET_RANGE_S,
        Math.min(LIMITS.SUBTITLE_OFFSET_RANGE_S, seconds),
      );
      desiredOffset = clamped;
      const active = activeSubtitleTrack();
      if (active) {
        applyToTrack(active);
      }
      // No active track yet: the change listener re-applies when one shows.
    },
    getOffset(): number {
      return desiredOffset;
    },
    reset(): void {
      // Reverse every applied shift so captions return to their original timing.
      for (let i = 0; i < textTracks.length; i++) {
        const track = textTracks[i] as TextTrack;
        if (!SUBTITLE_KINDS.has(track.kind)) continue;
        const applied = appliedByTrack.get(track);
        if (applied !== undefined && applied !== 0) {
          shiftCues(track, -applied);
          appliedByTrack.delete(track);
        }
        pendingByTrack.delete(track);
      }
      desiredOffset = 0;
    },
  };
}
